import { useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";

import { UploadProviderScheduleParams } from "~/services/admin-providers/types";
import {
  DropzoneContainer,
  EmptyCsvState,
  HiddenFileInput,
  Input,
  UploadedState,
} from "~/ui";

export const CsvUploadField = ({
  control,
}: {
  control: Control<UploadProviderScheduleParams>;
}) => {
  const [fileName, setFileName] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (
    files: FileList | null,
    onChange: (file: File | null) => void,
  ) => {
    if (!files?.length) return;

    const file = files[0];
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setValidationError("Only CSV files are allowed!");
      return;
    }

    setValidationError(undefined);
    setFileName(file.name);
    onChange(file);
  };

  const clearFile = (
    e: React.MouseEvent<HTMLButtonElement>,
    onChange: (file: File | null) => void,
  ) => {
    e.stopPropagation();
    setFileName("");
    setValidationError(undefined);
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const openFileDialog = () => inputRef.current?.click();
  return (
    <div className="flex w-full flex-col gap-2">
      <span className="text-sm text-neutral-800">
        Upload a schedule template
      </span>

      <Controller
        control={control}
        name="file"
        render={({ field }) => (
          <DropzoneContainer
            className="cursor-pointer"
            onDropFiles={(files) => handleFiles(files, field.onChange)}
            onClick={openFileDialog}
          >
            <Input
              id="csv-upload-field"
              error={validationError}
              value=""
              readOnly
              placeholder=""
              className="relative flex min-h-40 flex-col items-center justify-center gap-4 border-dashed"
            >
              <HiddenFileInput
                ref={inputRef}
                onChange={(files) => handleFiles(files, field.onChange)}
              />

              {!fileName ? (
                <EmptyCsvState />
              ) : (
                <UploadedState
                  fileName={fileName}
                  onClear={(e) => clearFile(e, field.onChange)}
                />
              )}
            </Input>
          </DropzoneContainer>
        )}
      />

      <span className="text-sm text-neutral-500">Supported formats: CSV</span>
    </div>
  );
};
